#!/usr/bin/env node
// CLI entry point for zenstack-custom codegen pipeline
import * as fs from "fs";
import * as path from "path";

import type { ZenConfig } from "./codegen/config";
import { discoverEnums, discoverTables, discoverViews } from "./codegen/discover";
import {
    emitEnumsTs,
    emitIndexTs,
    emitInputTs,
    emitModelsTs,
    emitSchemaTs,
    emitValidationTs,
    emitViewsTs,
} from "./codegen/emit";
import { buildRelations } from "./codegen/relations";
import { resolveConfig } from "./codegen/resolve";
import { buildTypeDefs, emitJsonRawTs, emitJsonTs } from "./codegen/typed-json";
import { emitGeneratedFileBanner, writeFileIfChanged } from "./codegen/utils";

export interface CodegenOptions {
    /** SQL column names to ignore when matching composite FK signatures (e.g. 'app_slug' for RLS) */
    ignoreCompositeFkColumns?: string[];
}

export async function runCodegen(config: ZenConfig, options: CodegenOptions = {}, configFilePath?: string): Promise<void> {
    const resolved = resolveConfig(config);
    const outDir = path.resolve(process.cwd(), config.schema.output);

    // 1. Discover
    const enumMap = discoverEnums(config.schema.enums);
    const tables = discoverTables(config.tables, resolved.computedFields, resolved.derivedModels);
    const views = config.schema.views ? discoverViews(config.schema.views) : [];

    // 2. Typed JSON (if types provided)
    let typesPath: string | undefined;
    if (typeof config.schema.types === "string") {
        typesPath = path.resolve(process.cwd(), config.schema.types);
    } else if (config.schema.types && configFilePath) {
        // types is an imported module — resolve types.ts relative to config file
        const configDir = path.dirname(path.resolve(process.cwd(), configFilePath));
        const candidate = path.join(configDir, "types.ts");
        const fs = await import("node:fs");
        if (fs.existsSync(candidate)) {
            typesPath = candidate;
        }
    }

    let typeDefs = new Map();
    let fieldToTypeDef = new Map();
    let typedJsonFields: import("./codegen/typed-json.types").TypedJsonFieldInfo[] = [];

    if (typesPath) {
        const artifacts = buildTypeDefs(typesPath, tables, views.length > 0 ? views : undefined, enumMap);
        typeDefs = artifacts.typeDefs;
        fieldToTypeDef = artifacts.fieldToTypeDef;
        typedJsonFields = artifacts.typedJsonFields;
    }

    // 3. Build relations
    const { modelRelations, inverseRelations } = buildRelations(
        tables,
        resolved.relationTargets,
        resolved.filteredRelations,
        resolved.throughRelations,
        resolved.fkRelations,
        { ignoreCompositeFkColumns: options.ignoreCompositeFkColumns },
    );

    // 4. Emit files
    const branding = {
        fkRelations: resolved.fkRelations,
        brandedFields: resolved.brandedFields,
        allTables: tables,
    };

    const schemaContent = emitSchemaTs({
        tables,
        enumMap,
        modelRelations,
        inverseRelations,
        searchDefaults: resolved.searchDefaults,
        derivedModels: resolved.derivedModels,
        modelScopes: resolved.modelScopes,
        typeDefs,
        fieldToTypeDef,
        views,
        derivedFields: resolved.derivedFields,
        branding,
        polymorphics: resolved.polymorphics,
        rls: config.rls,
    });

    const indexContent = emitIndexTs({
        tables,
        views,
        enumMap,
        typeDefs,
        typedJsonFields,
        branding,
        polymorphics: resolved.polymorphics,
    });

    const modelsContent = emitModelsTs(tables, typedJsonFields, branding, resolved.derivedFields, resolved.polymorphics);
    const inputContent = emitInputTs(tables);
    const validationContent = emitValidationTs(tables, enumMap, branding, resolved.polymorphics);
    const enumsContent = emitEnumsTs(enumMap);
    const jsonContent = emitJsonTs(typeDefs, typedJsonFields);

    const viewsContent = views.length > 0 ? emitViewsTs(views, typedJsonFields) : null;
    const jsonRawContent = typedJsonFields.some((f) => f.sourceTypeName)
        ? emitJsonRawTs(typedJsonFields)
        : null;

    // Emit plugins.ts with derived fields (compute functions serialized inline)
    let pluginsContent: string | null = null;
    if (Object.keys(resolved.derivedFields).length > 0) {
        pluginsContent = emitPluginsTs(tables, resolved.derivedFields);
    }

    // 5. Write to outDir
    fs.mkdirSync(outDir, { recursive: true });

    writeFileIfChanged(path.join(outDir, "schema.ts"), schemaContent);
    writeFileIfChanged(path.join(outDir, "index.ts"), indexContent);
    writeFileIfChanged(path.join(outDir, "models.ts"), modelsContent);
    writeFileIfChanged(path.join(outDir, "input.ts"), inputContent);
    writeFileIfChanged(path.join(outDir, "validation.ts"), validationContent);
    writeFileIfChanged(path.join(outDir, "enums.ts"), enumsContent);
    writeFileIfChanged(path.join(outDir, "json.ts"), jsonContent);

    if (viewsContent) {
        writeFileIfChanged(path.join(outDir, "views.ts"), viewsContent);
    }
    if (jsonRawContent) {
        writeFileIfChanged(path.join(outDir, "json-raw.ts"), jsonRawContent);
    }
    if (pluginsContent) {
        writeFileIfChanged(path.join(outDir, "plugins.ts"), pluginsContent);
    }

    console.log(`Generated schema files in ${outDir}`);
}

function emitPluginsTs(
    tables: Array<{ modelName: string; derivedFrom: string | null }>,
    derivedFields: Record<string, Record<string, { needs: Record<string, true>; compute: (...args: any[]) => any }>>,
): string {
    const uncapitalize = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);
    const tablesByModel = new Map(tables.map((table) => [table.modelName, table]));
    const getEffectiveDerivedFields = (modelName: string) => {
        const result: Record<string, { needs: Record<string, true>; compute: (...args: any[]) => any }> = {};
        const visited = new Set<string>();

        const visit = (currentModel: string) => {
            if (visited.has(currentModel)) return;
            visited.add(currentModel);

            const table = tablesByModel.get(currentModel);
            if (table?.derivedFrom) {
                visit(table.derivedFrom);
            }

            Object.assign(result, derivedFields[currentModel] ?? {});
        };

        visit(modelName);
        return result;
    };
    const lines = [
        ...emitGeneratedFileBanner(),
        '',
        'import { definePlugin } from "@zenstackhq/orm";',
        'import { schema } from "./schema";',
        '',
        'export const derivedFieldsPlugin = definePlugin(schema, {',
        '  id: "derived-fields",',
        '  result: {',
    ];

    for (const table of tables) {
        const fields = getEffectiveDerivedFields(table.modelName);
        if (Object.keys(fields).length === 0) continue;
        lines.push(`    ${uncapitalize(table.modelName)}: {`);
        for (const [field, def] of Object.entries(fields)) {
            const needsStr = JSON.stringify(def.needs);
            let computeStr = def.compute.toString();
            computeStr = computeStr.replace(
                /^(\(?)(\w+)(\)?\s*=>)/,
                (_match, _open, param, rest) => `(${param}: any)${rest.trimStart().startsWith('=>') ? rest.trimStart() : ' ' + rest.trimStart()}`,
            );
            computeStr = computeStr.replace(
                /^\(([^)]+)\)\s*=>/,
                (_, params) => {
                    const typed = params.split(',').map((p: string) => {
                        const trimmed = p.trim();
                        return trimmed.includes(':') ? trimmed : `${trimmed}: any`;
                    }).join(', ');
                    return `(${typed}) =>`;
                },
            );
            lines.push(`      ${field}: {`);
            lines.push(`        needs: ${needsStr},`);
            lines.push(`        compute: ${computeStr},`);
            lines.push(`      },`);
        }
        lines.push(`    },`);
    }

    lines.push('  } as any,');
    lines.push('});');
    lines.push('');
    return lines.join('\n');
}

async function main() {
    const args = process.argv.slice(2);
    const flags: Record<string, string> = {};
    const positional: string[] = [];

    for (const arg of args) {
        if (arg.startsWith("--")) {
            const [key, ...rest] = arg.slice(2).split("=");
            flags[key!] = rest.join("=") || "true";
        } else {
            positional.push(arg);
        }
    }

    const subcommand = positional[0];
    const filePath = positional[1];

    if (subcommand !== "generate" || !filePath) {
        console.error("Usage: zen-orm generate <config-file> [--ignore-cfk=col1,col2]");
        process.exit(1);
    }

    const codegenOptions: CodegenOptions = {};
    if (flags["ignore-cfk"]) {
        codegenOptions.ignoreCompositeFkColumns = flags["ignore-cfk"].split(",").map((s) => s.trim());
    }

    const resolvedPath = path.resolve(process.cwd(), filePath);

    // If config is a .ts file and we're not already running under tsx, re-exec via tsx
    if (resolvedPath.endsWith(".ts") && !process.env["__ZEN_TSX_REEXEC"]) {
        const { execFileSync } = await import("node:child_process");
        const tsxBin = await findTsx();
        if (!tsxBin) {
            console.error("Error: tsx is required to load .ts config files. Install it: pnpm add -D tsx");
            process.exit(1);
        }
        const cliPath = new URL(import.meta.url).pathname.replace(/^\/([A-Za-z]:)/, "$1");
        try {
            execFileSync(tsxBin, [cliPath, ...process.argv.slice(2)], {
                stdio: "inherit",
                shell: process.platform === "win32",
                env: { ...process.env, __ZEN_TSX_REEXEC: "1" },
            });
        } catch {
            process.exit(1);
        }
        return;
    }

    const { pathToFileURL } = await import("node:url");
    const importPath = resolvedPath.match(/^[A-Za-z]:/) ? pathToFileURL(resolvedPath).href : resolvedPath;
    const mod = await import(importPath);
    const config = mod.default ?? mod;
    await runCodegen(config, codegenOptions, filePath);
}

async function findTsx(): Promise<string | null> {
    const fs = await import("node:fs");
    // Check local node_modules/.bin/tsx first (works on all platforms)
    const localBin = path.resolve("node_modules", ".bin", process.platform === "win32" ? "tsx.CMD" : "tsx");
    if (fs.existsSync(localBin)) return localBin;
    // Check if tsx is globally available via PATH
    const { execFileSync } = await import("node:child_process");
    try {
        // process.platform-safe: use "where" on Windows, "which" elsewhere
        const cmd = process.platform === "win32" ? "where" : "which";
        return execFileSync(cmd, ["tsx"], { encoding: "utf8" }).trim().split("\n")[0] || null;
    } catch {
        return null;
    }
}

// Run main when executed as CLI
const isCLI =
    process.argv[1]?.endsWith("cli.js") ||
    process.argv[1]?.endsWith("cli.cjs") ||
    process.argv[1]?.includes("zenstack-custom") ||
    process.argv[1]?.includes("zen-orm");
if (isCLI) {
    main().catch((err) => {
        console.error(err);
        process.exit(1);
    });
}
